/**
 * MultiSelectTags Component
 *
 * IMPORTANT: Always explicitly set border colors to neutral-200
 * Default borders are black and look ugly. Use:
 * - border-neutral-200 (default state)
 * - border-neutral-300 (hover state)
 * - border-blue-500 (active/selected state)
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@/components/ui/icons/check-icon";
import { Button } from "@/components/ui/button";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface MultiSelectTagsProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const EMPTY_VALUE: string[] = [];

export function MultiSelectTags({
  options,
  value = EMPTY_VALUE,
  onChange,
  placeholder = "Select tags...",
  autoFocus = false,
}: MultiSelectTagsProps) {
  const [open, setOpen] = useState(autoFocus);

  const handleSelect = (option: string) => {
    const newValue = value.includes(option)
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  const handleRemove = (option: string) => {
    onChange(value.filter((v) => v !== option));
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-9 w-[250px] justify-between text-sm font-normal data-[placeholder]:text-neutral-400",
              dropdownTriggerClassName
            )}
          >
            <span className="truncate">
              {value.length > 0 ? `${value.length} selected` : placeholder}
            </span>
            <i
              className={cn(
                "fa-solid fa-chevron-down ml-2 h-4 w-4 shrink-0",
                dropdownChevronClassName
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[250px] p-0"
          align="start"
          disableAnimation
        >
          <Command>
            <CommandInput
              placeholder="Search tags..."
              className="h-9 text-neutral-900 placeholder:text-neutral-400 border-neutral-200"
            />
            <CommandList>
              <CommandEmpty className="text-neutral-500">
                No tags found.
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option}
                    onSelect={() => handleSelect(option)}
                    className="text-sm text-neutral-700"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        value.includes(option)
                          ? "bg-blue-500 border-blue-500 text-white"
                          : "border-neutral-300 bg-white [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon />
                    </div>
                    <span>{option}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((tag) => (
            <div
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-neutral-100 text-neutral-700 border border-neutral-200"
            >
              {tag}
              <button
                onClick={() => handleRemove(tag)}
                className="ml-1 hover:text-red-600 transition-colors"
                type="button"
              >
                <i className="fa-solid fa-xmark h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
